import React, { useState, useEffect } from 'react';
import {
  Autocomplete, Box, Card, CardContent, Typography, IconButton, TextField,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, Snackbar, Alert, CircularProgress, Button, Chip, Switch, FormControlLabel,
  useTheme, useMediaQuery
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon, FilterAltOff as ClearIcon, Layers as LayersIcon } from '@mui/icons-material';
import { reportCardAPI, academicAPI } from '../../services/api';
import BulkAssignDialog from './sections/BulkAssignDialog';
import ModulePageHeader from '../common/ModulePageHeader';
import TablePagination from '../common/TablePagination';

const ASSIGNMENT_TYPE_OPTIONS = [
  { value: '1', label: 'Compulsory' },
  { value: '0', label: 'Elective' }
];

const ClassStreamSubjectConfig = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [assignments, setAssignments] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 15, total: 0 });
  const [classes, setClasses] = useState([]);
  const [streams, setStreams] = useState([]);
  const [classStreams, setClassStreams] = useState([]);
  const [subjectPapers, setSubjectPapers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [formData, setFormData] = useState({ class_id: '', stream_id: '', subject_paper_id: '', is_compulsory: true, pass_mark: 50, max_marks: 100, weight_percentage: 100 });
  const [filters, setFilters] = useState({ class_id: '', stream_id: '', subject_paper_id: '', is_compulsory: '' });
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dialogLoading, setDialogLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedClassStreams, setSelectedClassStreams] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchAssignments(1);
  }, [filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [classesRes, streamsRes, papersRes, classStreamsRes, subjectsRes] = await Promise.all([
        academicAPI.getAllClasses(),
        academicAPI.getStreams(),
        reportCardAPI.getSubjectPapers({ is_active: 1 }),
        academicAPI.getClassStreamsForFilter(),
        reportCardAPI.getActiveSubjects()
      ]);
      setClasses(Array.isArray(classesRes.data) ? classesRes.data : classesRes.data.data || []);
      setStreams(Array.isArray(streamsRes.data) ? streamsRes.data : streamsRes.data.data || []);
      setSubjectPapers(Array.isArray(papersRes.data) ? papersRes.data : papersRes.data.data || []);
      setClassStreams(Array.isArray(classStreamsRes.data) ? classStreamsRes.data : classStreamsRes.data.data || []);
      setSubjects(Array.isArray(subjectsRes.data) ? subjectsRes.data : subjectsRes.data.data || []);
      await fetchAssignments();
    } catch (error) {
      setSnackbar({ open: true, message: 'Error fetching data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async (page = 1, perPage = null) => {
    try {
      const params = { page, per_page: perPage || pagination.per_page };
      if (filters.class_id) params.class_id = filters.class_id;
      if (filters.stream_id) params.stream_id = filters.stream_id;
      if (filters.subject_paper_id) params.subject_paper_id = filters.subject_paper_id;
      if (filters.is_compulsory !== '') params.is_compulsory = filters.is_compulsory;
      const res = await reportCardAPI.getClassStreamPapers(params);
      if (res.data?.data) {
        setAssignments(res.data.data);
        setPagination({
          current_page: res.data.current_page,
          last_page: res.data.last_page,
          per_page: res.data.per_page,
          total: res.data.total,
        });
      } else {
        setAssignments(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error fetching assignments', severity: 'error' });
    }
  };

  const handleClassChange = (classId) => {
    setFormData({ ...formData, class_id: classId, stream_id: '' });
    const classHasStreams = classStreams.filter(cs => cs.class_id === parseInt(classId));
    console.log('Selected class:', classId);
    console.log('Class streams found:', classHasStreams);
    setSelectedClassStreams(classHasStreams);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDialogLoading(true);
    setErrors({});
    try {
      if (editMode) {
        await reportCardAPI.updateClassStreamPaper(currentAssignment.id, {
          is_compulsory: formData.is_compulsory,
          pass_mark: formData.pass_mark,
          max_marks: formData.max_marks,
          weight_percentage: formData.weight_percentage
        });
        setSnackbar({ open: true, message: 'Assignment updated successfully!', severity: 'success' });
      } else {
        // Validate stream requirement
        if (selectedClassStreams.length > 0 && !formData.stream_id) {
          setSnackbar({ open: true, message: 'Stream is required for this class', severity: 'error' });
          setDialogLoading(false);
          return;
        }
        await reportCardAPI.storeClassStreamPaper(formData);
        setSnackbar({ open: true, message: 'Assignment created successfully!', severity: 'success' });
      }
      setShowModal(false);
      resetForm();
      fetchAssignments();
    } catch (error) {
      if (error.response?.status === 422 && error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        setSnackbar({ open: true, message: 'Please fix the validation errors', severity: 'error' });
      } else {
        setSnackbar({ open: true, message: error.response?.data?.message || 'Error saving assignment', severity: 'error' });
      }
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await reportCardAPI.deleteClassStreamPaper(deleteDialog.id);
      setSnackbar({ open: true, message: 'Assignment deleted successfully!', severity: 'success' });
      setDeleteDialog({ open: false, id: null });
      fetchAssignments();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Error deleting assignment', severity: 'error' });
    }
  };

  const openModal = (assignment = null) => {
    if (assignment) {
      setEditMode(true);
      setCurrentAssignment(assignment);
      setFormData({ 
        class_id: assignment.class_id, 
        stream_id: assignment.stream_id, 
        subject_paper_id: assignment.subject_paper_id, 
        is_compulsory: assignment.is_compulsory, 
        pass_mark: assignment.pass_mark, 
        max_marks: assignment.max_marks, 
        weight_percentage: assignment.weight_percentage 
      });
    } else {
      setEditMode(false);
      setCurrentAssignment(null);
      setFormData({ class_id: '', stream_id: '', subject_paper_id: '', is_compulsory: true, pass_mark: 50, max_marks: 100, weight_percentage: 100 });
    }
    setErrors({});
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ class_id: '', stream_id: '', subject_paper_id: '', is_compulsory: true, pass_mark: 50, max_marks: 100, weight_percentage: 100 });
    setEditMode(false);
    setCurrentAssignment(null);
  };

  const clearFilters = () => {
    setFilters({ class_id: '', stream_id: '', subject_paper_id: '', is_compulsory: '' });
  };

  return (
    <Box>
      <ModulePageHeader
        title="Class-Stream Subject Configuration"
        subtitle="Assign subject papers to class streams with curriculum settings"
        actions={[
          <Button key="bulk-assign" variant="outlined" startIcon={<LayersIcon />} onClick={() => setBulkDialog(true)} fullWidth={isMobile}>
            Bulk Assign Subject Papers
          </Button>,
          <Button key="clear-filters" variant="outlined" startIcon={<ClearIcon />} onClick={clearFilters} fullWidth={isMobile}>
            Clear Filters
          </Button>,
          <Button key="refresh" variant="outlined" startIcon={<RefreshIcon />} onClick={fetchAssignments} fullWidth={isMobile}>
            Refresh
          </Button>,
          <Button key="add-assignment" variant="contained" startIcon={<AddIcon />} onClick={() => openModal()} fullWidth={isMobile}>
            Add Assignment
          </Button>
        ]}
      />

      <Card elevation={3}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
            <Typography variant="h6">Assignments</Typography>
            <Chip label={assignments.length} size="small" color="primary" />
          </Box>

          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
            <Autocomplete
              sx={{ minWidth: { xs: 220, sm: 240, md: 260 } }}
              size="small"
              options={classes}
              value={classes.find((cls) => Number(cls.id) === Number(filters.class_id)) || null}
              onChange={(_, value) => setFilters({ ...filters, class_id: value?.id || '' })}
              getOptionLabel={(option) => option.name || option.class_name || 'Class'}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label="Class" />}
            />
            <Autocomplete
              sx={{ minWidth: { xs: 220, sm: 240, md: 260 } }}
              size="small"
              options={[{ id: 'null', name: 'No Stream' }, ...streams]}
              value={[{ id: 'null', name: 'No Stream' }, ...streams].find((option) => String(option.id) === String(filters.stream_id)) || null}
              onChange={(_, value) => setFilters({ ...filters, stream_id: value?.id || '' })}
              getOptionLabel={(option) => option.name || option.stream_name || 'Stream'}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              renderInput={(params) => <TextField {...params} label="Stream" />}
            />
            <Autocomplete
              sx={{ minWidth: { xs: 260, sm: 300, md: 340 } }}
              size="small"
              options={subjectPapers}
              value={subjectPapers.find((paper) => Number(paper.id) === Number(filters.subject_paper_id)) || null}
              onChange={(_, value) => setFilters({ ...filters, subject_paper_id: value?.id || '' })}
              getOptionLabel={(paper) => `${paper.subject?.subject_name || 'Subject'} - ${paper.paper_name || 'Paper'}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label="Subject Paper" />}
            />
            <Autocomplete
              sx={{ minWidth: { xs: 200, sm: 220, md: 240 } }}
              size="small"
              options={ASSIGNMENT_TYPE_OPTIONS}
              value={ASSIGNMENT_TYPE_OPTIONS.find((option) => option.value === String(filters.is_compulsory)) || null}
              onChange={(_, value) => setFilters({ ...filters, is_compulsory: value?.value || '' })}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => <TextField {...params} label="Type" />}
            />
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Class</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Stream</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Subject</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Paper</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Pass Mark</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Max Marks</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Weight %</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : assignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8, color: 'text.secondary' }}>
                      No assignments found. Click the + icon to add an assignment.
                    </TableCell>
                  </TableRow>
                ) : (
                  assignments.map((assignment) => (
                    <TableRow key={assignment.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{assignment.class?.name || '-'}</TableCell>
                      <TableCell>{assignment.stream?.name || 'No Stream'}</TableCell>
                      <TableCell>{assignment.subject_paper?.subject?.subject_name || '-'}</TableCell>
                      <TableCell>{assignment.subject_paper?.paper_name || '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={assignment.is_compulsory ? 'Compulsory' : 'Elective'} 
                          color={assignment.is_compulsory ? 'primary' : 'default'} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>{assignment.pass_mark}</TableCell>
                      <TableCell>{assignment.max_marks}</TableCell>
                      <TableCell>{assignment.weight_percentage}%</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => openModal(assignment)} color="primary">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setDeleteDialog({ open: true, id: assignment.id })} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            pagination={pagination}
            onPageChange={(page) => fetchAssignments(page)}
            onPageSizeChange={(newPerPage) => fetchAssignments(1, newPerPage)}
            isMobile={isMobile}
          />
        </CardContent>
      </Card>

      <Dialog open={showModal} onClose={() => setShowModal(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editMode ? 'Edit Assignment' : 'Assign Subject Paper to Class Stream'}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Autocomplete
              fullWidth
              disabled={editMode}
              options={classes}
              value={classes.find((cls) => Number(cls.id) === Number(formData.class_id)) || null}
              onChange={(_, value) => handleClassChange(value?.id || '')}
              getOptionLabel={(option) => option.name || option.class_name || 'Class'}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  label="Class"
                  required
                  error={!!errors.class_id}
                  helperText={errors.class_id?.[0]}
                />
              )}
            />
            <Autocomplete
              fullWidth
              disabled={editMode}
              options={selectedClassStreams.length === 0
                ? [{ id: '', name: 'No Stream' }]
                : selectedClassStreams.map((cs) => ({
                  id: cs.stream_id,
                  name: streams.find((stream) => stream.id === cs.stream_id)?.name || 'Unknown Stream'
                }))}
              value={(() => {
                if (selectedClassStreams.length === 0 && String(formData.stream_id) === '') {
                  return { id: '', name: 'No Stream' };
                }
                return selectedClassStreams
                  .map((cs) => ({
                    id: cs.stream_id,
                    name: streams.find((stream) => stream.id === cs.stream_id)?.name || 'Unknown Stream'
                  }))
                  .find((option) => String(option.id) === String(formData.stream_id)) || null;
              })()}
              onChange={(_, value) => setFormData({ ...formData, stream_id: value?.id ?? '' })}
              getOptionLabel={(option) => option.name || 'Stream'}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  label={`Stream ${selectedClassStreams.length > 0 ? '(Required)' : '(Optional)'}`}
                  required={selectedClassStreams.length > 0}
                  error={!!errors.stream_id}
                  helperText={errors.stream_id?.[0]}
                />
              )}
            />
            <Autocomplete
              fullWidth
              disabled={editMode}
              options={subjectPapers}
              value={subjectPapers.find((paper) => Number(paper.id) === Number(formData.subject_paper_id)) || null}
              onChange={(_, value) => setFormData({ ...formData, subject_paper_id: value?.id || '' })}
              getOptionLabel={(paper) => `${paper.subject?.subject_name || 'Subject'} - ${paper.paper_name || 'Paper'}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  label="Subject Paper"
                  required
                  error={!!errors.subject_paper_id}
                  helperText={errors.subject_paper_id?.[0]}
                />
              )}
            />
            <FormControlLabel
              control={<Switch checked={formData.is_compulsory} onChange={e => setFormData({ ...formData, is_compulsory: e.target.checked })} />}
              label="Compulsory"
              sx={{ mt: 2 }}
            />
            <TextField
              label="Pass Mark"
              type="number"
              value={formData.pass_mark}
              onChange={e => setFormData({ ...formData, pass_mark: e.target.value })}
              fullWidth
              margin="normal"
              error={!!errors.pass_mark}
              helperText={errors.pass_mark?.[0]}
            />
            <TextField
              label="Maximum Marks"
              type="number"
              value={formData.max_marks}
              onChange={e => setFormData({ ...formData, max_marks: e.target.value })}
              fullWidth
              margin="normal"
              error={!!errors.max_marks}
              helperText={errors.max_marks?.[0]}
            />
            <TextField
              label="Weight Percentage"
              type="number"
              value={formData.weight_percentage}
              onChange={e => setFormData({ ...formData, weight_percentage: e.target.value })}
              fullWidth
              margin="normal"
              error={!!errors.weight_percentage}
              helperText={errors.weight_percentage?.[0]}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowModal(false)} disabled={dialogLoading}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={dialogLoading}
            startIcon={dialogLoading ? <CircularProgress size={20} /> : null}
          >
            {dialogLoading ? 'Saving...' : editMode ? 'Update' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this assignment? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <BulkAssignDialog
        open={bulkDialog}
        onClose={() => setBulkDialog(false)}
        classes={classes}
        streams={classStreams}
        subjects={subjects}
        onSubmit={async (assignments) => {
          setBulkLoading(true);
          try {
            const res = await reportCardAPI.bulkAssignClassStreamPapers(assignments);
            const data = res.data?.data || [];
            const created = data.filter(d => d.status === 'created').length;
            const skipped = data.filter(d => d.status === 'exists').length;
            setSnackbar({
              open: true,
              message: `Done: ${created} created${skipped > 0 ? `, ${skipped} already existed (skipped)` : ''}`,
              severity: created > 0 ? 'success' : 'warning'
            });
            setBulkDialog(false);
            fetchAssignments();
          } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || 'Bulk assign failed', severity: 'error' });
          } finally {
            setBulkLoading(false);
          }
        }}
        loading={bulkLoading}
      />
    </Box>
  );
};

export default ClassStreamSubjectConfig;
